import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import { Copy, Users, DollarSign, TrendingUp, Crown, Share2, AlertCircle, User, Mail, ShoppingCart } from "lucide-react";
import { useReferralEarnings } from "@/hooks/useReferralEarnings";
import { useReferralCode } from "@/hooks/useReferralCode";
import { useUserRole } from "@/hooks/useUserRole";
import { useReferredUsers } from "@/hooks/useReferredUsers";
import { formatCurrency } from "@/lib/utils";
import LoadingSpinner from "@/components/LoadingSpinner";

export const ReferralDashboard = () => {
  const { role, loading: roleLoading, isPractitioner, isPartner } = useUserRole();
  const { stats, earnings, practitionerEarnings, isLoading } = useReferralEarnings();
  const { referralLink, copyReferralLink, copying, codeStatus, hasNoCode } = useReferralCode();
  const { referredUsers, totalReferredUsers, isLoading: usersLoading } = useReferredUsers();

  // Check if user loading
  if (roleLoading || isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <LoadingSpinner />
      </div>
    );
  }

  // Show message if user is not a practitioner or partner (e.g., admin viewing this page)
  if (!isPractitioner && !isPartner) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col space-y-2">
          <h1 className="text-3xl font-bold tracking-tight">Referral Program</h1>
          <p className="text-muted-foreground">
            Earn recurring revenue share on every dollar your referred users spend
          </p>
        </div>

        <Card className="border-blue-200 bg-blue-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-blue-600" />
              Admin View
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground">
              You are viewing this page as an <strong>{role}</strong>. The referral program is only available for practitioners and partners.
            </p>
            <p className="text-sm text-muted-foreground">
              To manage referral codes for practitioners, please use the <strong>Referral Management</strong> section in the admin dashboard.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="p-8 text-center">
        <p className="text-muted-foreground">Unable to load referral data</p>
      </div>
    );
  }

  const progressToNextTier = stats.nextTier 
    ? (stats.activeReferrals / stats.nextTier.min_active_referrals) * 100
    : 100;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col space-y-2">
        <h1 className="text-3xl font-bold tracking-tight">Referral Program</h1>
        <p className="text-muted-foreground">
          Earn {stats.currentTier.revenue_share_percentage}% recurring revenue share on every dollar your referred users spend
        </p>
      </div>

      {/* Current Tier & Referral Link */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2">
              <Crown className="h-5 w-5" />
              Current Tier
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <Badge variant="secondary" className="text-lg px-3 py-1">
                {stats.currentTier.tier_name}
              </Badge>
              <span className="font-semibold">
                {stats.currentTier.revenue_share_percentage}% revenue share
              </span>
            </div>
            
            {stats.nextTier && (
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Progress to {stats.nextTier.tier_name}</span>
                  <span>{stats.activeReferrals}/{stats.nextTier.min_active_referrals} active referrals</span>
                </div>
                <Progress value={progressToNextTier} className="h-2" />
                <p className="text-xs text-muted-foreground">
                  {stats.nextTier.min_active_referrals - stats.activeReferrals} more active referrals needed for {stats.nextTier.revenue_share_percentage}% revenue share
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Share2 className="h-5 w-5" />
                Your Referral Link
              </CardTitle>
              {codeStatus && (
                <Badge
                  variant={codeStatus === 'active' ? 'default' : 'destructive'}
                  className="text-xs"
                >
                  {codeStatus}
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {codeStatus === 'revoked' && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg mb-2">
                <p className="text-sm text-red-800">
                  Your referral code has been revoked. Please contact your administrator for more information.
                </p>
              </div>
            )}
            <div className="p-3 bg-muted rounded-lg border">
              <p className="text-sm font-mono break-all">
                {referralLink || 'No referral link available'}
              </p>
            </div>
            <Button
              onClick={copyReferralLink}
              disabled={copying || !referralLink}
              className="w-full"
              variant="outline"
            >
              <Copy className="h-4 w-4 mr-2" />
              {copying ? 'Copying...' : 'Copy Link'}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Stats Overview */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-5">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Earnings</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(stats.totalEarnings)}</div>
            <p className="text-xs text-muted-foreground">All time</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">This Month</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(stats.monthlyEarnings)}</div>
            <p className="text-xs text-muted-foreground">Current month</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Signups</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalReferrals}</div>
            <p className="text-xs text-muted-foreground">Users who signed up</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Paying Customers</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{practitionerEarnings?.paying_referrals || 0}</div>
            <p className="text-xs text-muted-foreground">Users who made purchases</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Referrals</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.activeReferrals}</div>
            <p className="text-xs text-muted-foreground">Purchased in 30 days</p>
          </CardContent>
        </Card>
      </div>

      {/* Recent Earnings */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Earnings</CardTitle>
        </CardHeader>
        <CardContent>
          {earnings.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">
              No earnings yet. Start sharing your referral link to earn commissions!
            </p>
          ) : (
            <div className="space-y-4">
              {earnings.slice(0, 10).map((earning) => (
                <div key={earning.id} className="flex items-center justify-between">
                  <div className="space-y-1">
                    <p className="text-sm font-medium">
                      Session Purchase
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(earning.created_at).toLocaleDateString()} • {earning.tier_at_time} Tier ({earning.revenue_share_percentage}%)
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium">
                      {formatCurrency(earning.earnings_amount)}
                    </p>
                    <Badge 
                      variant={earning.payout_status === 'paid' ? 'default' : 'secondary'}
                      className="text-xs"
                    >
                      {earning.payout_status}
                    </Badge>
                  </div>
                </div>
              ))}
              
              {earnings.length > 10 && (
                <>
                  <Separator />
                  <p className="text-center text-sm text-muted-foreground">
                    And {earnings.length - 10} more earnings...
                  </p>
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Referred Users List */}
      <Card>
        <CardHeader>
          <CardTitle>Your Referred Users</CardTitle>
        </CardHeader>
        <CardContent>
          {usersLoading ? (
            <div className="flex items-center justify-center py-8">
              <LoadingSpinner />
            </div>
          ) : referredUsers.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">
              No users have signed up with your referral code yet. Start sharing your link to grow your network!
            </p>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {totalReferredUsers} {totalReferredUsers === 1 ? 'user has' : 'users have'} signed up using your referral code
              </p>
              <div className="space-y-3">
                {referredUsers.map((user) => (
                  <div
                    key={user.user_id}
                    className="p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">
                            {user.full_name || 'User'}
                          </span>
                          {user.has_made_purchase && (
                            <Badge variant="default" className="text-xs">
                              <ShoppingCart className="h-3 w-3 mr-1" />
                              Customer
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Mail className="h-4 w-4" />
                          <a
                            href={`mailto:${user.email}`}
                            className="hover:underline hover:text-foreground transition-colors"
                          >
                            {user.email}
                          </a>
                        </div>
                        {user.referral_applied_at && (
                          <p className="text-xs text-muted-foreground">
                            Joined {new Date(user.referral_applied_at).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                      <div className="text-right space-y-1">
                        <div className="text-sm">
                          <p className="text-muted-foreground">Total Spent</p>
                          <p className="font-semibold">{formatCurrency(user.total_spent)}</p>
                        </div>
                        {user.total_earnings > 0 && (
                          <div className="text-sm">
                            <p className="text-muted-foreground">Your Earnings</p>
                            <p className="font-semibold text-green-600">{formatCurrency(user.total_earnings)}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* How It Works */}
      <Card>
        <CardHeader>
          <CardTitle>How Referral Earnings Work</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-3">
            <div className="text-center space-y-2">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                <Share2 className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-semibold">Share Your Link</h3>
              <p className="text-sm text-muted-foreground">
                Copy your unique referral link and share it with potential users
              </p>
            </div>
            <div className="text-center space-y-2">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                <Users className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-semibold">Users Sign Up</h3>
              <p className="text-sm text-muted-foreground">
                When someone signs up using your link, they're permanently attributed to you
              </p>
            </div>
            <div className="text-center space-y-2">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                <DollarSign className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-semibold">Earn Recurring Revenue</h3>
              <p className="text-sm text-muted-foreground">
                Get {stats.currentTier.revenue_share_percentage}% of every dollar they spend, forever
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};